import { envConfigs } from '@config/envConfigs';
import { IOption, IPagedResultsV1 } from '@gcl/react-core';
import { useQuery } from '@tanstack/react-query';

import { AxiosErrorResponse } from '@core/error.models';
import { IApiBaseProps, IPaginatedOptionsApiProps, IPaginatedOptionSearchApiProps } from './api.models';
import { isValidApiBaseProps } from './api.services';
import { DEFAULT_OPTION_ID_FIELD_NAME, DEFAULT_OPTION_TEXT_FIELD_NAME } from './constants';

import { isPositiveNumber } from '../core/Primitives/number.services';
import { IUseFetchOptionsPropsBase } from './SelectAsyncPaginate';
import { apiServerGet } from './axios.services';

export const createGetOptionsCustomPropsFn = <T, K extends IApiBaseProps>(apiRoute: string) => {
  return async (props: K): Promise<IPagedResultsV1<T>> => {
    const result = await apiServerGet<IPagedResultsV1<T>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
      params: {
        ...props,
      },
    });
    return result.data;
  };
};

export const createGetOptionsFn = <T>(apiRoute: string) => {
  return async (props: IApiBaseProps): Promise<IPagedResultsV1<T>> => {
    const result = await apiServerGet<IPagedResultsV1<T>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
      params: {
        ...props,
      },
    });
    return result.data;
  };
};

export const createGetOptionsNoPropsFn = <T>(apiRoute: string) => {
  return async (): Promise<IPagedResultsV1<T>> => {
    const result = await apiServerGet<IPagedResultsV1<T>>(`${envConfigs.apiBaseUrl}${apiRoute}`);
    return result.data;
  };
};

export interface IHandleOptionsProps {
  idFieldName?: string | undefined;
  textFieldName?: string | undefined;
  shouldConvertOptions?: boolean;
}

export const toOption = <T>(
  item: T,
  idFieldName = DEFAULT_OPTION_ID_FIELD_NAME,
  textFieldName = DEFAULT_OPTION_TEXT_FIELD_NAME,
) => {
  const tempItem = item as any;

  return {
    key: tempItem[idFieldName],
    text: tempItem[textFieldName],
    data: item,
    value: item,
  } as IOption<T>;
};

export interface ICreateUseFetchOptionsFnPropsBase {
  queryName: string;
  handleOptionsProps?: IHandleOptionsProps | undefined;
  enabled: boolean;
}

export interface ICreateUseFetchOptionsCustomPropsFnProps<T, K extends IApiBaseProps>
  extends ICreateUseFetchOptionsFnPropsBase {
  getOptionsFn: (props: K) => Promise<IPagedResultsV1<T>>;
}

export const createUseFetchOptionsCustomPropsFn = <T, K extends IApiBaseProps>({
  queryName,
  getOptionsFn,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
}: ICreateUseFetchOptionsCustomPropsFnProps<T, K>) => {
  return (props: K, enabled = true) =>
    useQuery<IOption<T>[], AxiosErrorResponse>(
      [props.projectId, props, queryName],
      async () => {
        const result = await getOptionsFn(props);
        return (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : (item as IOption<typeof item>);
        });
      },
      {
        enabled: isValidApiBaseProps(props) && enabled,
      },
    );
};

export interface ICreateUseFetchOptionsFnProps<T> extends ICreateUseFetchOptionsFnPropsBase {
  getOptionsFn: (props: IApiBaseProps) => Promise<IPagedResultsV1<T>>;
}

export const createUseFetchOptionsFn = <T>({
  queryName,
  getOptionsFn,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
}: ICreateUseFetchOptionsFnProps<T>) => {
  return (props: IApiBaseProps, enabled = true) =>
    useQuery<IOption<T>[], AxiosErrorResponse>(
      [props.projectId, queryName],
      async () => {
        const result = await getOptionsFn(props);
        return (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : (item as IOption<typeof item>);
        });
      },
      {
        enabled: isValidApiBaseProps(props) && enabled,
      },
    );
};

export interface ICreateUseFetchOptionsNoPropsFnProps<T> extends ICreateUseFetchOptionsFnPropsBase {
  getOptionsFn: () => Promise<IPagedResultsV1<T>>;
}

export const createUseFetchOptionsNoPropsFn = <T>({
  queryName,
  getOptionsFn,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
  enabled,
}: ICreateUseFetchOptionsNoPropsFnProps<T>) => {
  return () =>
    useQuery<IOption<T>[] | T[], AxiosErrorResponse>(
      [queryName],
      async () => {
        const result = await getOptionsFn();
        return (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : (item as IOption<typeof item>);
        });
      },
      {
        enabled,
      },
    );
};

export const createUseFetchCheckBoxOptionsFn = <T>({
  queryName,
  getOptionsFn,
  enabled = true,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
}: ICreateUseFetchOptionsFnProps<T>) => {
  return (props: IApiBaseProps) =>
    useQuery<(T | IOption<T>)[], AxiosErrorResponse>(
      [props.projectId, queryName],
      async () => {
        const result = await getOptionsFn(props);

        return (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : item;
        });
      },
      {
        enabled: isValidApiBaseProps(props) && enabled,
      },
    );
};

export const createGetOptionsWithSearchFn = <T>(apiRoute: string) => {
  return async (props: IPaginatedOptionsApiProps): Promise<IPagedResultsV1<T>> => {
    const result = await apiServerGet<IPagedResultsV1<T>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
      params: {
        ...props,
      },
    });
    return result.data;
  };
};

export const createGetOptionsCustomPropsWithSearchFn = <TResponse, TRequest>(apiRoute: string) => {
  return async (props: TRequest): Promise<IPagedResultsV1<TResponse>> => {
    const result = await apiServerGet<IPagedResultsV1<TResponse>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
      params: {
        ...props,
      },
    });
    return result.data;
  };
};

export interface ICreateUseFetchOptionsWithSearchFnProps<T> extends ICreateUseFetchOptionsFnPropsBase {
  getOptionsWithSearchFn: (props: IPaginatedOptionsApiProps) => Promise<IPagedResultsV1<T>>;
}

type TGetOptionsCustomPropsWithSearchFn<T extends IUseFetchOptionsPropsBase> = Omit<T, 'pageFilter'>;

export interface ICreateUseFetchOptionsCustomPropsWithSearchFnProps<
  TResponse,
  TRequest extends IUseFetchOptionsPropsBase,
> extends ICreateUseFetchOptionsFnPropsBase {
  getOptionsWithSearchFn: (
    props: TGetOptionsCustomPropsWithSearchFn<TRequest> & IPaginatedOptionSearchApiProps,
  ) => Promise<IPagedResultsV1<TResponse>>;
}

export interface IUseFetchOptionsWithSearchProps extends IUseFetchOptionsPropsBase {
  projectId: number;
  projectTeamIds?: number[];
}

export const createUseFetchOptionsWithSearchFn = <T>({
  queryName,
  getOptionsWithSearchFn,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
  enabled,
}: ICreateUseFetchOptionsWithSearchFnProps<T>) => {
  return ({ keyword, pageFilter, projectId, projectTeamIds }: IUseFetchOptionsWithSearchProps) => {
    return useQuery(
      [JSON.stringify(projectTeamIds), projectId, queryName, keyword, pageFilter?.skip, pageFilter?.take],
      async () => {
        const pageSize = pageFilter?.take ?? -1;
        const pageNumber = (pageFilter?.skip ?? -1) / pageSize + 1;
        const result = await getOptionsWithSearchFn({ projectId, pageSize, pageNumber, keyword, projectTeamIds });

        const options = (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : (item as IOption<typeof item>);
        });

        return {
          options,
          pageSize,
          pageNumber,
          totalItemCount: result.totalCount || 0,
        };
      },

      {
        enabled:
          isPositiveNumber(pageFilter?.skip ?? -1, true) &&
          isPositiveNumber(pageFilter?.take ?? -1, true) &&
          isPositiveNumber(projectId || -1) &&
          enabled,
      },
    );
  };
};

export const createUseFetchOptionsCustomPropsWithSearchFn = <TResponse, TRequest extends IUseFetchOptionsPropsBase>({
  queryName,
  getOptionsWithSearchFn,
  handleOptionsProps = {
    idFieldName: DEFAULT_OPTION_ID_FIELD_NAME,
    textFieldName: DEFAULT_OPTION_TEXT_FIELD_NAME,
    shouldConvertOptions: true,
  },
  enabled,
}: ICreateUseFetchOptionsCustomPropsWithSearchFnProps<TResponse, TRequest>) => {
  return (props: TRequest) => {
    const { keyword, pageFilter, ...rest } = props;
    return useQuery(
      [JSON.stringify(rest), queryName, keyword, pageFilter?.skip, pageFilter?.take],
      async () => {
        const pageSize = pageFilter?.take ?? -1;
        const pageNumber = (pageFilter?.skip ?? -1) / pageSize + 1;
        const result = await getOptionsWithSearchFn({ ...props, pageNumber, pageSize });

        const options = (result.items || []).map((item) => {
          return handleOptionsProps.shouldConvertOptions
            ? toOption(item, handleOptionsProps.idFieldName, handleOptionsProps.textFieldName)
            : (item as IOption<typeof item>);
        });

        return {
          options,
          pageSize: result.totalCount || 0,
          pageNumber: 1,
          totalItemCount: result.totalCount || 0,
        };
      },

      {
        enabled,
      },
    );
  };
};
